import { useQueryClient } from '@tanstack/react-query';
import {
  Chat01RoomType,
  GetPersonaDetailsResponse,
  PostEmojiRequest,
} from '~/types';
import {
  GetGameResponse,
  PostGameEmojiRequestData,
} from '~/types/missionGameApi';
import characterKey from '../character/characterKey';
import missionGameKey from '../missionGame/missionGameKey';

const useUpdateEmoji = () => {
  const queryClient = useQueryClient();
  const EMOJI_TYPES = ['like', 'cool', 'surprise', 'tough', 'angry', 'money'];

  // 이모지 추가 및 업데이트 기능
  const updateEmojiLike = (
    request: PostEmojiRequest | PostGameEmojiRequestData,
  ) => {
    const isMissionGame = 'game_id' in request;

    // 캐시에서 기존 데이터 가져오기
    const prev = isMissionGame
      ? queryClient.getQueryData<GetGameResponse>(
          missionGameKey.getGame({
            game_id: (request as PostGameEmojiRequestData).game_id,
          }),
        )
      : queryClient.getQueryData<GetPersonaDetailsResponse>(
          characterKey.getPersonaDetails({
            character_id: (request as PostEmojiRequest).character_id,
          }),
        );

    if (prev) {
      const { emojis } = prev.data;

      // emoji_id로 이모지가 이미 존재하는지 확인
      const emojiIndex = emojis.findIndex(
        (emoji) => emoji.emoji_id === request.emoji_id, // request의 emoji_id로 비교
      );

      let updatedEmojis;

      // 존재하지 않는다면 새로운 이모지 추가
      if (emojiIndex === -1) {
        // Ensure that the emoji_type exists for the provided emoji_id
        const emojiType = EMOJI_TYPES[request.emoji_id - 1];

        if (!emojiType) {
          console.error(`Invalid emoji_id: ${request.emoji_id}`);
          return;
        }

        updatedEmojis = [
          ...emojis,
          {
            emoji_id: request.emoji_id, // 요청에 있는 emoji_id로 추가
            emoji_type: emojiType, // Corresponding emoji_type from EMOJI_TYPES
            count: 1,
            is_clicked: true,
          },
        ];
      } else {
        // 존재하는 이모지라면 count 증가 및 is_clicked 업데이트
        updatedEmojis = emojis.map((emoji, index) =>
          index === emojiIndex
            ? {
                ...emoji,
                count: emoji.count + 1,
                is_clicked: true,
              }
            : emoji,
        );
      }

      // 업데이트된 데이터로 캐시 갱신
      const updatedPersonaData = {
        ...prev,
        data: {
          ...prev.data,
          emojis: updatedEmojis,
        },
      };

      queryClient.setQueryData(
        isMissionGame
          ? missionGameKey.getGame({
              game_id: (request as PostGameEmojiRequestData).game_id,
            })
          : characterKey.getPersonaDetails({
              character_id: (request as PostEmojiRequest).character_id,
            }),
        updatedPersonaData,
      );
    } else {
      console.error('No cached data found for queryKey:');
    }
  };

  // 이모지 취소(삭제) 기능
  const deleteEmojiLike = (
    request: PostEmojiRequest | PostGameEmojiRequestData,
  ) => {
    const isMissionGame = 'game_id' in request;

    // 캐시에서 기존 데이터 가져오기
    const prev = queryClient.getQueryData<GetPersonaDetailsResponse>(
      isMissionGame
        ? missionGameKey.getGame({
            game_id: (request as PostGameEmojiRequestData).game_id,
          })
        : characterKey.getPersonaDetails({
            character_id: (request as PostEmojiRequest).character_id,
          }),
    );

    if (prev) {
      const { emojis } = prev.data;

      let updatedEmojis;

      // 이모지 취소 처리
      const existingEmoji = emojis.find(
        (emoji) => emoji.emoji_id === request.emoji_id,
      );

      // count가 1보다 크면 count 감소, 1이면 배열에서 제거
      if (existingEmoji && existingEmoji.count > 1) {
        updatedEmojis = emojis.map((emoji) =>
          emoji.emoji_id === request.emoji_id
            ? {
                ...emoji,
                count: emoji.count - 1,
                is_clicked: false,
              }
            : emoji,
        );
      } else {
        updatedEmojis = emojis.filter(
          (emoji) => emoji.emoji_id !== request.emoji_id,
        );
      }

      // 업데이트된 데이터로 캐시 갱신
      const updatedPersonaData = {
        ...prev,
        data: {
          ...prev.data,
          emojis: updatedEmojis,
        },
      };

      queryClient.setQueryData(
        isMissionGame
          ? missionGameKey.getGame({
              game_id: (request as PostGameEmojiRequestData).game_id,
            })
          : characterKey.getPersonaDetails({
              character_id: (request as PostEmojiRequest).character_id,
            }),
        updatedPersonaData,
      );
    } else {
      console.error('No cached data found for queryKey:');
    }
  };

  return {
    updateEmojiLike,
    deleteEmojiLike,
  };
};

export default useUpdateEmoji;
